import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { FlexiItem, FlexiSelectionMode } from '../types'

interface Props {
  items: FlexiItem[]
  selectionMode: FlexiSelectionMode
  selectedCodes: string[]
  selectedPlanCode: string
  onToggle: (itemCode: string) => void
  onSelectSingle: (itemCode: string | null) => void
}

const formatCurrency = (amount: number): string => '₹' + amount.toLocaleString('en-IN')

const resolveFlexiPremium = (
  item: FlexiItem,
  planCode: string
): number => {
  if (item.planPremiums) {
    const match = item.planPremiums.find((p) => p.planCode === planCode)
    if (match) return match.premiumAmount
  }
  return item.premiumAmount
}

const FlexiSelector: React.FC<Props> = ({
  items,
  selectionMode,
  selectedCodes,
  selectedPlanCode,
  onToggle,
  onSelectSingle,
}) => {
  const [expandedTerms, setExpandedTerms] = useState<string[]>([])

  if (items.length === 0) return null

  const isSingle = selectionMode === 'Single'

  const toggleTerms = (itemCode: string) => {
    setExpandedTerms((prev) =>
      prev.includes(itemCode) ? prev.filter((c) => c !== itemCode) : [...prev, itemCode]
    )
  }

  // Coverage comparison table
  const hasCoverage = items.some((i) => i.coverageDetails && i.coverageDetails.length > 0)
  const coverageLabels = hasCoverage
    ? Array.from(
        new Set(
          items.flatMap((i) => (i.coverageDetails || []).map((d) => d.label))
        )
      )
    : []

  return (
    <div className='mb-6'>
      <label className='form-label fw-bolder fs-6 mb-4'>Flexi Benefits</label>

      {/* Coverage comparison table */}
      {hasCoverage && coverageLabels.length > 0 && (
        <div className='card card-bordered mb-4 shadow-sm' style={{ border: '1px solid #d0d5dd' }}>
          <div className='card-body p-0'>
            <div className='table-responsive'>
              <table className='table table-row-bordered table-row-gray-200 align-middle fs-8 mb-0'>
                <thead>
                  <tr className='border-bottom border-gray-200'>
                    <th className='bg-light fw-bolder ps-4 py-3' style={{ minWidth: '170px' }}>Feature</th>
                    {items.map((item) => {
                      const isSelected = selectedCodes.includes(item.itemCode)
                      return (
                        <th
                          key={item.itemCode}
                          className={`text-center align-middle py-3 cursor-pointer border-start ${
                            isSelected
                              ? 'text-white'
                              : 'bg-light-primary text-primary'
                          }`}
                          style={{ minWidth: '120px', ...(isSelected ? { backgroundColor: '#198754' } : {}) }}
                          onClick={() => (isSingle ? onSelectSingle(item.itemCode) : onToggle(item.itemCode))}
                        >
                          <span className='fw-bolder fs-7'>{item.displayName}</span>
                          {isSelected && <i className='bi bi-check-circle-fill ms-2' style={{ color: '#fff' }}></i>}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* SI Row */}
                  <tr>
                    <td className='fw-bold ps-4 py-3'>Sum Insured</td>
                    {items.map((item) => {
                      const isSelected = selectedCodes.includes(item.itemCode)
                      return (
                        <td
                          key={item.itemCode}
                          className={`text-center py-3 border-start cursor-pointer ${
                            isSelected ? 'bg-light-success fw-bolder' : ''
                          }`}
                          onClick={() => (isSingle ? onSelectSingle(item.itemCode) : onToggle(item.itemCode))}
                        >
                          {item.sumInsuredLabel || '—'}
                        </td>
                      )
                    })}
                  </tr>
                  {/* Premium Row */}
                  <tr className='bg-gray-100'>
                    <td className='fw-bold ps-4 py-3'>Premium</td>
                    {items.map((item) => {
                      const isSelected = selectedCodes.includes(item.itemCode)
                      return (
                        <td
                          key={item.itemCode}
                          className={`text-center fw-bolder fs-7 py-3 border-start cursor-pointer ${
                            isSelected ? 'text-white' : 'text-primary'
                          }`}
                          style={isSelected ? { backgroundColor: '#198754' } : {}}
                          onClick={() => (isSingle ? onSelectSingle(item.itemCode) : onToggle(item.itemCode))}
                        >
                          {formatCurrency(resolveFlexiPremium(item, selectedPlanCode))}
                        </td>
                      )
                    })}
                  </tr>
                  {/* Coverage details */}
                  {coverageLabels.map((label) => (
                    <tr key={label}>
                      <td className='ps-4 py-3'>{label}</td>
                      {items.map((item) => {
                        const detail = item.coverageDetails?.find((d) => d.label === label)
                        const isSelected = selectedCodes.includes(item.itemCode)
                        const val = detail?.value || '—'
                        return (
                          <td
                            key={item.itemCode}
                            className={`text-center py-3 border-start cursor-pointer ${
                              isSelected ? 'bg-light-success fw-bold' : ''
                            } ${val === 'NA' || val === '—' ? 'text-muted' : 'text-dark'}`}
                            onClick={() => (isSingle ? onSelectSingle(item.itemCode) : onToggle(item.itemCode))}
                          >
                            {val}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                  {/* Select button row */}
                  <tr className='border-top border-gray-300'>
                    <td className='ps-4 py-4'></td>
                    {items.map((item) => {
                      const isSelected = selectedCodes.includes(item.itemCode)
                      return (
                        <td key={item.itemCode} className='text-center py-4 border-start'>
                          <button
                            className={`btn btn-sm px-4 ${
                              isSelected ? 'text-white' : 'btn-outline-primary'
                            }`}
                            style={isSelected ? { backgroundColor: '#198754', borderColor: '#198754' } : {}}
                            onClick={(e) => {
                              e.stopPropagation()
                              isSingle ? onSelectSingle(item.itemCode) : onToggle(item.itemCode)
                            }}
                          >
                            {isSelected ? '✓ Selected' : 'Select'}
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Flexi options grid */}
      <div className='row g-3 mt-2'>
        {/* No flexi option (single mode) */}
        {isSingle && (
          <div className='col-12 col-md-6'>
            <div
              className={`border rounded px-4 py-3 cursor-pointer h-100 ${
                selectedCodes.length === 0
                  ? 'border-primary bg-light-primary'
                  : 'border-gray-300 bg-white'
              }`}
              onClick={() => onSelectSingle(null)}
            >
              <div className='d-flex align-items-center'>
                <Form.Check
                  type='radio'
                  name='flexi-select'
                  checked={selectedCodes.length === 0}
                  readOnly
                  className='me-3'
                />
                <div className='fw-bold'>No Flexi Benefit</div>
              </div>
            </div>
          </div>
        )}

        {/* Flexi items */}
        {items.map((item) => {
          const isSelected = selectedCodes.includes(item.itemCode)
          const premium = resolveFlexiPremium(item, selectedPlanCode)
          const inputType = isSingle ? 'radio' : 'checkbox'
          const isExpanded = expandedTerms.includes(item.itemCode)

          return (
            <div key={item.itemCode} className={isExpanded ? 'col-12' : 'col-12 col-md-6'}>
              <div
                className={`border rounded px-4 py-3 cursor-pointer h-100 ${
                  isSelected ? 'border-primary bg-light-primary' : 'border-gray-300 bg-white'
                }`}
                onClick={() => (isSingle ? onSelectSingle(item.itemCode) : onToggle(item.itemCode))}
              >
                <div className='d-flex align-items-center justify-content-between flex-wrap'>
                  <div className='d-flex align-items-center flex-wrap'>
                    <Form.Check
                      type={inputType}
                      name={isSingle ? 'flexi-select' : `flexi-${item.itemCode}`}
                      checked={isSelected}
                      readOnly
                      className='me-3'
                    />
                    <span className='fw-bolder fs-6 me-2'>{item.displayName}</span>
                    {item.termsHtml && (
                      <button
                        className='btn btn-link btn-sm p-0 text-muted fs-8 ms-2'
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleTerms(item.itemCode)
                        }}
                      >
                        {isExpanded ? 'Hide Terms' : 'View Terms'}
                      </button>
                    )}
                  </div>
                  <div className='fw-bold text-primary fs-6'>{formatCurrency(premium)}</div>
                </div>

                {/* Expanded terms */}
                {item.termsHtml && isExpanded && (
                  <div
                    className='mt-2 p-3 bg-light rounded fs-8 ms-6'
                    dangerouslySetInnerHTML={{ __html: item.termsHtml }}
                  />
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default FlexiSelector
